import { useMemo } from 'react';
import { Modal } from '@safetyhub/ui';
import { EditModalFooter } from '../common/EditModalFooter';
import { isValidIdentityPhone, normalizePhoneInput } from '../../lib/phone';
import { MODAL_LAYER, SUB_MODAL_OVERLAY_BG, type ModalLayer } from '../modalLayers';

/**
 * 결재자·담당자 인원(외부 인원 = 회사 Member)을 새로 등록하거나 연락처를 보정하는 폼의 **단일 정본**.
 *
 * ⚠️ 이 폼을 화면마다 복사하지 말 것. 예전에 세 곳에 복사돼 있었고, 연락처 규약이 바뀌자
 * 라벨이 세 곳에서 따로 낡았다("(하나 이상 필수)"가 남아 있었다). 지금 진입로는 결재라인 편집
 * 모달(`useApproverMemberForm` — 외부 Member 등록·연락처 보정) 하나다.
 * 이 폼이 만드는 것은 **회사 Member**다(별도 결재자 신원 축은 없다).
 *
 * 형상은 **하나**다 — 이름·휴대폰·이메일. 옵션 축(직위 칸·외부 결재자 토글·`requirePhone`·
 * 수정 모드 dirty 게이트·저장 라벨)을 다시 만들지 말 것: 호출부가 기본값만 넘기면 그 축은
 * 그대로 죽는다. 신규 등록과 연락처 보정 두 모드는 제목·설명만 다르고(호출부 `isFixingContact`가
 * 가른다) 입력·검증은 같다.
 *
 * 전화번호는 **신원 키**라 항상 필수다(2026-08-11 공통 규약) — 결재 요청을 이메일로 보내는 것과는
 * 별개 축이고, 보정 경로(`updateMemberContact`)도 phone을 필수로 받는다.
 */
export interface ApproverContactFormValue {
  name: string;
  email?: string;
  phone?: string;
}

export interface ApproverContactFormModalProps {
  open: boolean;
  /** 모달 제목. */
  heading: string;
  /** 제목 아래 보조 설명(선택). */
  description?: string;
  value: ApproverContactFormValue;
  error?: string;
  isSaving?: boolean;
  /**
   * 다른 모달 위에 겹쳐 뜨는 sub 모달이면 true — z-[80] + 옅은 오버레이.
   * 두 오버레이가 겹치면 알파가 곱해져 단독 모달보다 어두워지기 때문(모달 z-index 정책).
   */
  isSubModal?: boolean;
  /**
   * 겹침 층 지정(isSubModal일 때만 유효, 기본 `MODAL_LAYER.sub`).
   * 이 폼 위에 다른 모달이 이미 떠 있으면 여는 쪽이 더 높은 층을 준다.
   */
  layer?: ModalLayer;
  onChange: (updates: Partial<ApproverContactFormValue>) => void;
  onSave: () => void;
  onClose: () => void;
}

const CONTROL_CLASS =
  'block w-full px-3 py-2 border border-line rounded-control shadow-card-sm focus:ring-brand-500 focus:border-brand-500';

/** 저장 버튼 라벨 — 신규 등록·연락처 보정 둘 다 결과는 「결재라인에 지정」이라 같은 동사다. */
const SUBMIT_LABEL = '등록';

/**
 * 푸터 — 취소가 base Modal의 requestClose로 수렴한다.
 * context는 Modal children **하위 컴포넌트**에서만 잡히므로 분리했다(base Modal 규약).
 */
function ApproverContactFooter({
  blockedReason,
  canSubmit,
  isSaving,
  onSave,
  onFallbackClose,
}: {
  blockedReason?: string;
  canSubmit: boolean;
  isSaving: boolean;
  onSave: () => void;
  onFallbackClose: () => void;
}) {
  return (
    <EditModalFooter
      isDirty={false}
      blockedReason={blockedReason ?? ''}
      submitLabel={SUBMIT_LABEL}
      submitDisabled={isSaving || !canSubmit}
      busy={isSaving}
      onSubmit={onSave}
      onFallbackClose={onFallbackClose}
    />
  );
}

export function ApproverContactFormModal({
  open,
  heading,
  description,
  value,
  error,
  isSaving = false,
  layer,
  isSubModal = false,
  onChange,
  onSave,
  onClose,
}: ApproverContactFormModalProps) {
  // Chrome 주소 자동완성이 이름/연락처 칸을 가로채는 걸 막는다. 재렌더링마다 바뀌면 포커스를
  // 잃으므로 마운트 시 한 번만 정한다.
  const fieldNames = useMemo(() => {
    const suffix = Math.random().toString(36).slice(2, 8);
    return {
      name: `approver-new-name-${suffix}`,
      email: `approver-new-email-${suffix}`,
      phone: `approver-new-phone-${suffix}`,
    };
  }, []);

  // 저장이 막힌 **이유**를 문장으로 들고 있는다. 이유 없이 비활성만 시키면 사용자는 왜 안 되는지
  // 알 길이 없다 — 기존에는 클릭해야 뜨는 에러였는데, 클릭 자체가 안 되니 영영 보이지 않았다.
  // 적다 만 번호도 막는다 — 그대로 보내면 서버 @Matches에 걸려 400이 되고, 사용자는 모달 안에서
  // 이유를 못 본다.
  const blockedReason = !value.name.trim()
    ? '이름을 입력해주세요.'
    : !isValidIdentityPhone(value.phone)
      ? '휴대폰 번호를 올바르게 입력해주세요. (같은 사람인지 가리는 기준입니다)'
      : undefined;
  const canSubmit = !blockedReason;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={heading}
      size="md"
      {...(isSubModal
        ? {
            // 층은 여는 쪽이 정한다 — 이 폼은 상세(root 위=sub)에서도, 위임 흐름의 결재라인
            // 편집 모달(sub2) 위에서도 열린다. 후자에서 sub로 뜨면 편집 모달 뒤로 숨는다.
            overlayZClassName: layer ?? MODAL_LAYER.sub,
            overlayBgClassName: SUB_MODAL_OVERLAY_BG,
          }
        : {})}
      footer={
        <ApproverContactFooter
          blockedReason={blockedReason}
          canSubmit={canSubmit}
          isSaving={isSaving}
          onSave={onSave}
          onFallbackClose={onClose}
        />
      }
    >
      {description && <p className="text-xs text-ink-500 mb-4">{description}</p>}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-card">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <form autoComplete="off" onSubmit={(e) => e.preventDefault()} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-ink-700">
            이름 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={value.name}
            onChange={(e) => onChange({ name: e.target.value })}
            autoComplete="new-password"
            name={fieldNames.name}
            className={`mt-1 ${CONTROL_CLASS}`}
            placeholder="홍길동"
          />
        </div>

        <div className="border-t border-line-soft pt-4">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">
                휴대폰 <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                inputMode="numeric"
                value={value.phone || ''}
                // 보이는 값 자체를 숫자만 유지한다(`lib/phone.ts` 규약 — 전송 경계와 같은 규칙).
                onChange={(e) => onChange({ phone: normalizePhoneInput(e.target.value) })}
                autoComplete="off"
                name={fieldNames.phone}
                className={`${CONTROL_CLASS} text-sm`}
                placeholder="01012345678"
              />
              <p className="mt-1 text-xs text-ink-400">
                같은 사람인지 가리는 기준이라 반드시 입력해야 합니다.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">이메일</label>
              <input
                type="email"
                value={value.email || ''}
                onChange={(e) => onChange({ email: e.target.value })}
                autoComplete="off"
                name={fieldNames.email}
                className={`${CONTROL_CLASS} text-sm`}
                placeholder="example@company.com"
              />
            </div>
          </div>
          <p className="mt-2 text-xs text-ink-400">
            결재 요청 시 입력된 연락처로 링크가 발송됩니다.
          </p>
        </div>
      </form>
    </Modal>
  );
}
